/**
 * ExpireBanner — barra de licença fixa no rodapé (porta de `showExpire` [application.js:3481]),
 * no visual da proposta "Elements" (17/10/2026): fina, de ponta a ponta, com a luz e o texto do
 * selo da licença da Home. Estados:
 *  - 4 dias ou mais: luz verde, "Licença ativa até DATA · N dias";
 *  - menos de 4 dias (inclusive entre 24 e 48 h, que antes caía na contagem): luz âmbar + Renovar;
 *  - último dia: fundo creme, contagem HH:MM:SS que anda + Renovar;
 *  - vencida: fundo vermelho claro, "Licença encerrada em DATA…" + Renovar;
 *  - vitalícia (>10 anos) / assinatura (≥366 dias): luz verde e o texto próprio.
 * O div.notice segue para o global.css posicionar no pé e esconder no celular.
 *
 * Movimento (23/10/2026): a luz pulsa com o .ds-luz nos ritmos da Home (M3 a): ativa 3,2 s,
 * perto de vencer e último dia 1,8 s (.ds-perto), vencida 1,1 s (.ds-vencida); para com "reduzir
 * movimento". As cores de cada estado seguem as de antes. A contagem HH:MM:SS do último dia é
 * informação, não animação: troca a cada segundo, com algarismos de largura igual.
 * Leitor de tela (Laços de estado): a barra não é mais role="status", para a contagem não ser
 * anunciada a cada segundo; só a troca de estado (ativa, perto, último dia, vencida) é anunciada,
 * uma vez, numa região à parte que se esvazia depois.
 */
import { useEffect, useState } from 'react';
import { useSessionStore } from '../../stores/sessionStore';
import { useBrand } from '../../hooks/useBrand';
import { genPromo } from '../../lib/promo';
import { externalUrls } from '../../config/env';
import { emitActivity } from '../../lib/activity';
import { Html } from '../screen/Screen';
import { useScreenText } from '../screen/screenText';
import '../screen/screen.css';

const DAY = 60 * 60 * 24;
/** Tempo que o aviso da troca de estado fica na região anunciada (não é movimento). */
const AVISO_MS = 10000;

function two(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

type BarState = 'ok' | 'warn' | 'today' | 'ended';

/** Ritmo da luz por estado (o mesmo do selo da licença na Home). */
const LIGHT: Record<BarState, string> = {
  ok: '',
  warn: ' ds-perto',
  today: ' ds-perto',
  ended: ' ds-vencida',
};

export function ExpireBanner(): JSX.Element | null {
  const { tx, plural, date } = useScreenText();
  const brand = useBrand();
  const session = useSessionStore((s) => s.session);
  // Re-render por segundo somente no último dia (contagem regressiva).
  const [, setTick] = useState(0);

  const expiredAt = session?.expiredAt ? new Date(session.expiredAt) : null;
  const seconds =
    expiredAt && !Number.isNaN(expiredAt.getTime())
      ? Math.floor((expiredAt.getTime() - Date.now()) / 1000)
      : null;

  const countdown = seconds != null && seconds > 0 && seconds < DAY;
  useEffect(() => {
    if (!countdown) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [countdown]);

  const isLeadMachine = brand.source === 'lead-machine';
  let text = '';
  let state: BarState | null = null;

  if (session && seconds != null && expiredAt) {
    state = 'ok';
    if (seconds >= 366 * DAY) {
      text = seconds > 365 * DAY * 10 ? tx('lic_lifetime') : tx('lic_sub');
    } else if (seconds >= DAY) {
      const days = Math.floor(seconds / DAY);
      if (days < 4) state = 'warn';
      const vars = { date: date(expiredAt), days: plural('days', days) };
      text = isLeadMachine || state === 'ok' ? tx('lic_active', vars) : tx('lic_em', vars);
    } else if (seconds > 0) {
      let rest = seconds;
      const hours = Math.floor(rest / 3600);
      rest -= hours * 3600;
      const minutes = Math.floor(rest / 60);
      rest -= minutes * 60;
      text = tx('lic_today', { time: `${two(hours)}:${two(minutes)}:${two(rest)}` });
      state = 'today';
    } else {
      text = tx('lic_ended', { date: date(expiredAt) });
      state = 'ended';
    }
  }

  // Troca de estado durante a sessão: o texto daquele momento vai para a região anunciada, uma
  // vez. A barra que nasce não anuncia nada (como antes: conteúdo inicial de região viva não é lido).
  const [visto, setVisto] = useState<BarState | null>(state);
  const [aviso, setAviso] = useState('');
  if (state !== visto) {
    setVisto(state);
    if (state && visto) setAviso(text);
  }
  useEffect(() => {
    if (!aviso) return undefined;
    // Esvazia depois de lido, para o texto (com a hora daquele momento) não ficar velho.
    const id = window.setTimeout(() => setAviso(''), AVISO_MS);
    return () => window.clearTimeout(id);
  }, [aviso]);

  if (!state) return null;

  function renew(): void {
    emitActivity('renew.clicked', { where: 'banner' });
    // Legado [3563-3567]: lead-machine leva o email no link promo (desconto + atribuição).
    let url: string;
    if (isLeadMachine) {
      url = externalUrls.buyLeadMachine;
      if (session?.email) url += '/' + genPromo({ email: session.email });
    } else {
      url = externalUrls.buyEminer;
    }
    window.open(url, '_blank');
  }

  return (
    <div className={`notice as-root as-bar as-bar--${state}`}>
      <span className={`as-bar__light ds-luz${LIGHT[state]}`} aria-hidden="true" />
      <Html as="span" html={text} />
      {/* Texto dos arquivos de idioma (o mesmo do Html acima), anunciado só na troca de estado. */}
      <span className="as-sr" role="status" dangerouslySetInnerHTML={{ __html: aviso }} />
      {state !== 'ok' ? (
        <button type="button" className="as-bar__renew ds-resposta" onClick={renew}>
          {tx('renew')}
          <span className="material-symbols-rounded" aria-hidden="true">
            open_in_new
          </span>
        </button>
      ) : null}
    </div>
  );
}
